<template>
  <transition mode="out-in">
    <loading-spinner v-if="fetching" />
    <div v-else>
      <div v-if="eBayStatus?.authenticated" class="ms-5 mb-3">
        <div class="card bg-success-subtle">
          <div class="card-body">
            <h5 class="card-title">
              <span class="bi bi-shield-check me-1"></span>
              Access Granted
            </h5>
            <div>
              <button type="button" class="btn btn-link text-danger btn-sm" @click="revokeAccess">
                <span class="bi bi-ban me-1"></span>
                Revoke eBay Access
              </button>
            </div>
          </div>
        </div>
      </div>

      <hr>

      <div>
        <div v-if="inSync === SYNC_NONE">
          <div class="lead mb-3">
            <i class="bi bi-exclamation-triangle text-warning me-2"></i>
            You Don't Have an eBay Store to Import.
          </div>
        </div>

        <div v-else-if="inSync === SYNC_MISMATCH">
          <div class="lead mb-3">
            <i class="bi bi-2-circle text-info me-2"></i>
            Connect eBay
          </div>
          <div class="ms-5 mb-3">
            <h5>
              <i class="bi bi-exclamation-triangle text-warning me-2"></i>
              eBay Account Mismatch
            </h5>
            <p class="mb-3">
              You've authorized <strong>@{{ eBayDetails?.UserID }}</strong>, but your eShopCommand account
              is linked to <strong>@{{ eshop.id }}</strong>.
            </p>
            <p class="mb-3">
              Please revoke access and reconnect with the correct eBay account (@{{ eshop.id }}).
            </p>
          </div>
        </div>

        <div v-else-if="inSync === SYNC_OFF">
          <div class="lead mb-3">
            <i class="bi bi-2-circle text-info me-2"></i>
            Connect eBay
          </div>
          <div class="ms-5 mb-3">
            <h5>
              <span class="bi bi-arrow-repeat me-2"></span>
              Reconnect Your Account
            </h5>
            <p class="mb-3">
              <strong>@{{ eshop.id }}</strong> was previously disconnected.
            </p>
            <p class="mb-3">
              Reconnect to resume syncing your eBay data.
            </p>
            <button type="button" class="btn btn-primary" @click="reconnect">
              Reconnect eBay Account
            </button>
          </div>
        </div>

        <div v-else-if="inSync === SYNC_TRUE || inSync === SYNC_PENDING">
          <div class="lead mb-3">
            <i class="bi bi-check-circle text-success me-2"></i>
            eBay Connected.
          </div>
          <div class="ms-5">
          <e-shop-card :eshop="storeDetails" class="mb-3 bg-success-subtle">
            <template #header>
              <i class="bi bi-link-45deg me-2"></i>
              Connected eBay User/Store
            </template>
          </e-shop-card>
          </div>
        </div>

        <div v-else-if="inSync === SYNC_NO_IMPORT">

          <div class="lead mb-3">
            <i class="bi bi-circle text-info me-2"></i>
            Next: Connect Your eBay Store.
          </div>

          <div class="ms-5">
            <e-shop-card :eshop="storeDetails" class="mb-3">
              <template #header>
                <i class="bi bi-box-arrow-in-down me-2"></i>
                Ready to Connect:
              </template>
            </e-shop-card>

            <button type="button"
                    class="btn btn-primary"
                    @click="syncUp">
              <i class="bi bi-box-arrow-in-down me-2"></i> Connect eBay Store
            </button>
          </div>
        </div>

        <div v-else-if="inSync === SYNC_DIFFERENT">
          <div class="lead mb-3">
            <i class="bi bi-check-circle text-warning me-2"></i>
            eBay Store Imported - Needs Updating.
            <button type="button" class="ms-3 btn btn-sm btn-light btn-outline-primary" @click="syncUp">
              <i class="bi bi-arrow-down-up me-2"></i>
              Resync Now
            </button>
          </div>

          <div class="ms-5">
            <e-shop-card :eshop="storeDetails" class="mb-3">
              <template #header>
                <i class="bi bi-box-arrow-in-down me-2"></i>
                Connected eBay Store
              </template>
            </e-shop-card>

            <div class="text-center mb-3">
              <i class="display-1 bi bi-arrow-down-up"></i>
            </div>

            <e-shop-card v-if="eshop" :eshop="eshop" class="mb-3">
              <template #header>Imported eShop (Needs Updating)</template>
            </e-shop-card>
          </div>
        </div>

      </div>

      <hr>

      <div>
        <div v-if="inSync === SYNC_PENDING">
          <!-- Historical Data Import Section -->
          <div class="lead mb-3">
            <i class="me-2 bi bi-3-circle text-info"></i>
            Import Recent Sales
          </div>
          <div class="ms-5">
            <ebay-backfill @on-complete="onBackfillComplete" @on-get-started="onGetStarted" />
          </div>
        </div>
        <div v-else-if="inSync === SYNC_TRUE">
          <div class="lead mb-3">
            <i class="me-2 bi" :class="{'bi-check-circle text-success':eBayStatus?.imported, 'bi-3-circle text-info': !eBayStatus?.imported}"></i>
            Import Recent Sales
          </div>
          <div class="ms-5">
            <ebay-backfill @on-complete="onBackfillComplete" @on-get-started="onGetStarted" />
          </div>
        </div>
        <div v-else class="lead mb-5 text-muted">
          <i class="bi bi-3-circle me-2"></i>
          Import Recent Sales
        </div>
      </div>


    </div>
  </transition>
</template>

<script setup>
import EShopCard from "@/app/components/eShopCard.vue";
import {computed, ref, defineEmits, onMounted} from "vue";
import {useStore} from "vuex";
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import EbayBackfill from "@/app/components/ebay/EbayBackfill.vue";

const SYNC_NONE = 'none'
const SYNC_NO_IMPORT = 'no-import'
const SYNC_DIFFERENT = 'diff'
const SYNC_TRUE = 'in-sync'
const SYNC_OFF = 'off'
const SYNC_MISMATCH = 'mismatch'
const SYNC_PENDING = 'pending'

const store = useStore()
const storeDetails = ref()
const fetching = ref(false)
const eBayStatus = computed(() => store.state.eBayStatus)

const eBaySellerId = computed(() => {
  if (!eBayDetails.value?.SellerInfo?.StoreURL) return null
  return eBayDetails.value?.SellerInfo?.StoreURL.replace("https://www.ebay.com/str/",'')
})

const eshop = computed(() => store.state.eshop.eshop)
const inSync = computed(() => {
  if (!storeDetails.value) return SYNC_NONE
  if (!eshop.value) return SYNC_NO_IMPORT
  if (eshop.value.id !== eBayDetails.value?.UserID && eshop.value.id !== eBaySellerId.value) return SYNC_MISMATCH
  if (eshop.value.status === 'PAUSED' || eBayStatus.value?.connected === 'PAUSED') return SYNC_OFF
  if (eshop.value.status === 'PENDING' || eBayStatus.value?.connected === 'PENDING') return SYNC_PENDING
  if (eshop.value.id !== storeDetails.value.URLPath) return SYNC_DIFFERENT
  if (eshop.value.name !== storeDetails.value.Name) return SYNC_DIFFERENT
  if (eshop.value.description !== storeDetails.value.Description) return SYNC_DIFFERENT
  if (eshop.value.logoUrl !== storeDetails.value.Logo?.URL) return SYNC_DIFFERENT
  return SYNC_TRUE
})

const emits = defineEmits(['onError', 'onCompleted'])

onMounted(() => {
  getEbayStoreDetails()
  getEbayUserDetails()
})

function getEbayStoreDetails(sync) {
  fetching.value = true
  store.dispatch('eshop/getEbayStoreDetails', { syncData: !!sync })
      .then(details => {
        storeDetails.value = details
        if (sync) {
          // After syncing store, check backfill status
          //checkBackfillStatus()
        }
      })
      .catch(error => {
        emits('onError', error.response?.data || error.message || error)
      })
      .finally(() => {
        fetching.value = false
      })
}

function syncUp() {
  getEbayStoreDetails(true)
}

getEbayStatus()
async function getEbayStatus() {
  await store.dispatch("eshop/getEbayConnectionState")
}

async function reloadDetails() {
  await store.dispatch('eshop/loadStore')
  await getEbayStatus()
}

const revokeAccess = async () => {
  if (!confirm('Are you sure you want to revoke eBay access? This will stop all sync/imports from eBay and deactivate your eShop.')) return;

  try {
    const response = await store.state.api.post('/ebay/revoke');
    const result = await response.data;

    if (result.success) {
      await store.dispatch('eshop/clearEbayTokens')
    } else {
      // Network error - tokens still exist
      alert('revoke error: ' + (result.message || 'Failed to revoke access. Please try again.'));
    }
  } catch (err) {
    console.log(err)
    alert('Failed to revoke access. Please try again.');
  }
  await reloadDetails()
};

async function reconnect() {
  try {
    const response = await store.state.api.post('/ebay/reconnect', {eid: eBaySellerId.value || eBayDetails.value?.UserID});
    const result = await response.data;

    if (result.success === false) {
      alert('reconnecting error: ' + (result.message || 'Failed to reconnect. Please try again.'));
    }
  } catch (err) {
    console.log(err)
    alert('Failed to reconnect. Please try again.');
  }
  await reloadDetails()
}

function onBackfillComplete() {
  // Backfill finished - emit to parent
  emits('onCompleted')
}

function onGetStarted() {
  // User clicked Get Started - emit to parent for redirect
  emits('onCompleted')
}

// getEbayUserDetails()
const eBayDetails = ref()
function getEbayUserDetails(sync) {
  store.dispatch('eshop/getEbayUserDetails', { syncData: !!sync })
      .then(details => {
        eBayDetails.value = details;
        // userDetails.value = details
        // isEbayStore.value = !!details.SellerInfo?.StoreOwner
        // fetching.value = false
        // syncData.UserID = details.UserID || null
        // if (!isEbayStore.value) {
        //   syncData.id = details.UserID
        //   syncData.StoreID = details.UserID
        //   syncData.Name = details.SellerInfo?.SellerPaymentAddress?.Name || details.RegistrationAddress?.Name || null
        //   syncData.Description = 'eBay Individual Seller - No Store'
        // }
      })
      .catch(error => {
        emits('onError', error.response?.data || error.message || error)
        fetching.value = false
      })
}


</script>

<style lang="scss" scoped>

</style>
